/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import DashedDivider from 'components/style-elements/DashedDivider'
import styled, { keyframes } from 'styled-components'
import { Button, FlexBox, Icon, Text, Tile } from 'vspry-style-components'
import { Offer } from 'api/common'
import { useLocale } from 'context/localeContext'
import { formatDateReadable, formatISODateTimeToInputDate } from 'utils/formatters'
import OfferDescription from './OfferDescription'

type RoundedOfferTileProps = {
    offer: Offer
    isSelected: boolean
    onSelect: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    doActivate: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, link?: boolean) => Promise<void>
    onActivate?: () => Promise<void | null>
    activating: boolean
    linking: boolean
    today: string
}

const RoundThumbailImage = styled.img`
    border-radius: 15px;
    box-shadow: 3px 4px 4px 0 rgba(0, 0, 0, 0.25);
    width: 25vw;
    height: 100%;
    flex-shrink: 0;
    object-fit: cover;
`

const shine = keyframes`
  0% {
    transform: translate(150%, 150%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  10% {
    opacity: 1;
    transform: translate(0%, 0%) rotate(45deg);
  }
  15% {
    opacity: 0;
    transform: translate(-150%, -150%) rotate(45deg);
  }
  100% {
    opacity: 0;
    transform: translate(-150%, -150%) rotate(45deg);
  }
`

const ShineOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 150%;
    height: 150%;
    border-radius: 10px;
    background: linear-gradient(
        120deg,
        transparent 0%,
        rgba(255, 255, 255, 0.15) 40%,
        rgba(255, 255, 255, 0.25) 50%,
        rgba(255, 255, 255, 0.15) 60%,
        transparent 100%
    );
    transform: rotate(45deg);
    animation: ${shine} 15s ease-in infinite;
    z-index: 2;
`

const TileWrapper = styled.div`
    position: relative;
    overflow: hidden;
    border-radius: 15px;
`

const RoundTile = styled(Tile)`
    border-radius: 15px;
    /* min-width: 250px;
    width: 100%; */
    min-height: 54px;
    padding: 7px;
    transition: all 0.3s ease;
`

export default function RoundedOfferTile({ offer, isSelected, onSelect, doActivate, onActivate, activating, linking, today }: RoundedOfferTileProps) {
    const { translate } = useLocale()

    return (
        <TileWrapper>
            <RoundTile onClick={onSelect}>
                <FlexBox justify='start' gap='small'>
                    <RoundThumbailImage src={offer.imageURL} alt='offer-thumbnail' />
                    <FlexBox $column justify='center'>
                        <Text size='small' margin='no' $bold $left maxLines={isSelected ? undefined : 2} width='100%'>
                            {offer.title} {!isSelected ? <Icon name='chevron down' /> : <Icon name='chevron up' />}
                        </Text>
                        <Text size='xSmall' margin='no' $left>
                            {offer.productName ?? (
                                <>
                                    <strong>{translate('pages.offerTile.offerExpiry.text')}</strong> {formatDateReadable(new Date(offer.expiryDate))}
                                </>
                            )}
                        </Text>
                        {offer.startDate && new Date(offer.startDate).toISOString() > today && (
                            <Text $left size='xSmall'>
                                {translate('pages.specialOffers.starts')}: {formatISODateTimeToInputDate(offer.startDate)}
                            </Text>
                        )}
                    </FlexBox>
                </FlexBox>
                {isSelected && (
                    <>
                        <DashedDivider />
                        <FlexBox gap='small' $column>
                            <OfferDescription description={offer.description} />
                            {offer.productName && (
                                <Text size='xSmall' margin='no' $left>
                                    <strong>{translate('pages.offerTile.offerExpiry')}</strong> {formatDateReadable(new Date(offer.expiryDate))}
                                </Text>
                            )}
                            <Text size='xSmall' margin='no' $left>
                                <strong>{translate('pages.offerTile.useBy')}</strong>{' '}
                                {offer.activationLastDate
                                    ? formatDateReadable(new Date(offer.activationLastDate))
                                    : `${offer.activationLastDatePeriod} day(s)`}
                            </Text>
                            {onActivate && !(offer.url && offer.urlLabel) && (
                                <Button id='OT-acivate-btn' onClick={(e) => doActivate(e)} disabled={activating || linking} loading={activating}>
                                    {translate('buttons.activate')}
                                </Button>
                            )}
                            {offer.url && offer.urlLabel && (
                                <Button
                                    id='OT-doActivate-btn'
                                    onClick={(e) => {
                                        doActivate(e, true)
                                        if (offer.url) window.location.href = offer.url
                                    }}
                                    disabled={activating || linking}
                                    loading={linking}
                                >
                                    {offer.urlLabel}
                                </Button>
                            )}
                        </FlexBox>
                    </>
                )}
            </RoundTile>
            <ShineOverlay />
        </TileWrapper>
    )
}
